import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { UserCheck, Copy, Check, Phone, Mail, Calendar, Scissors } from "lucide-react";
import { toast } from "sonner";

/**
 * "Kunder å følge opp" — manual follow-up list.
 *
 * Backed by `GET /api/customers/follow-up`. Shown only when the active
 * business is on Pro (or Demo, so prospects experience the feature). No
 * automatic SMS/email is ever sent — every action is a one-tap copy that
 * the owner pastes into their own channel.
 *
 * Plan-gating: caller decides whether to render this component (see
 * Dashboard.jsx / Kunder.jsx). Backend returns 402 for Basic as a
 * defence-in-depth check.
 */
function fmtDate(iso) {
  if (!iso) return "—";
  return new Date(iso).toLocaleDateString("nb-NO", { dateStyle: "medium" });
}

function fmtDaysAgo(days) {
  if (days === null || days === undefined) return null;
  if (days < 60) return `for ${days} dager siden`;
  if (days < 365) {
    const months = Math.round(days / 30);
    return `for ca. ${months} ${months === 1 ? "måned" : "måneder"} siden`;
  }
  const years = Math.round(days / 365);
  return `for ca. ${years} ${years === 1 ? "år" : "år"} siden`;
}

// Modern-clipboard-with-legacy-fallback helper. The legacy textarea +
// execCommand path is critical for browsers that reject `writeText` with
// NotAllowedError (focus loss, blocked permission, non-secure context).
async function copyText(text) {
  const legacy = () => {
    const ta = document.createElement("textarea");
    ta.value = text;
    ta.style.position = "fixed";
    ta.style.opacity = "0";
    document.body.appendChild(ta);
    ta.select();
    const ok = document.execCommand("copy");
    document.body.removeChild(ta);
    return ok;
  };
  try {
    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(text);
      return true;
    }
    return legacy();
  } catch {
    return legacy();
  }
}

export default function FollowUpList({ variant = "card" }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [gatedOut, setGatedOut] = useState(false); // true when backend says 402 (Basic)
  const [busy, setBusy] = useState(null); // id of customer being snoozed
  const [justCopied, setJustCopied] = useState(null); // `${id}-msg` | `${id}-contact`

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/customers/follow-up");
      setData(r.data);
      setGatedOut(false);
    } catch (err) {
      if (err?.response?.status === 402) {
        // Active business is on Basic. Surface nothing — caller decides
        // whether to render the empty wrapper.
        setGatedOut(true);
        setData({ items: [], threshold_days: 60, suggested_message: "", count: 0 });
      } else {
        toast.error("Kunne ikke laste oppfølgingsliste");
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const flashCopied = (key) => {
    setJustCopied(key);
    setTimeout(() => setJustCopied((k) => (k === key ? null : k)), 1500);
  };

  const copyMessage = async (c) => {
    const text = (data?.suggested_message || "").replace(/\{navn\}/g, c.name);
    const ok = await copyText(text);
    if (ok) {
      flashCopied(`${c.id}-msg`);
      toast.success("Melding kopiert");
    } else {
      toast.error("Klarte ikke å kopiere");
    }
  };

  const copyContact = async (c) => {
    const parts = [c.name];
    if (c.phone) parts.push(c.phone);
    if (c.email) parts.push(c.email);
    const ok = await copyText(parts.join(" · "));
    if (ok) {
      flashCopied(`${c.id}-contact`);
      toast.success("Kontaktinfo kopiert");
    } else {
      toast.error("Klarte ikke å kopiere");
    }
  };

  const snooze = async (c) => {
    setBusy(c.id);
    try {
      await api.post(`/customers/${c.id}/follow-up/snooze`);
      // Optimistically remove from the local list — re-loading would also
      // work but feels slower.
      setData((d) => d ? { ...d, items: (d.items || []).filter((it) => it.id !== c.id) } : d);
      toast.success("Markert som fulgt opp");
    } catch {
      toast.error("Kunne ikke markere");
    } finally {
      setBusy(null);
    }
  };

  // When the active business is Basic, render nothing instead of an empty
  // card so the surrounding layout doesn't show a hollow shell.
  if (gatedOut) return null;

  return (
    <Card className="rounded-2xl border border-emerald-500/10 ring-1 ring-inset ring-emerald-500/5 shadow-[0_2px_30px_-12px_rgba(16,185,129,0.18)] dark:shadow-[0_2px_40px_-10px_rgba(16,185,129,0.22)] h-full flex flex-col" data-testid="followup-card">
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle className="font-heading flex items-center gap-2">
          <UserCheck className="w-4 h-4 text-primary" strokeWidth={1.5} />
          Kunder å følge opp
        </CardTitle>
        <span className="text-xs text-muted-foreground">
          Siste besøk eldre enn {data?.threshold_days ?? 60} dager
        </span>
      </CardHeader>
      <CardContent className="flex-1">
        {loading ? (
          <p className="text-sm text-muted-foreground py-6 text-center">Laster…</p>
        ) : !data?.items?.length ? (
          <p className="text-sm text-muted-foreground py-6 text-center" data-testid="followup-empty">
            Ingen kunder trenger oppfølging akkurat nå. 👌
          </p>
        ) : (
          <ul className="space-y-3">
            {data.items.slice(0, 8).map((c) => {
              const suggested = (data.suggested_message || "").replace(/\{navn\}/g, c.name);
              return (
                <li
                  key={c.id}
                  className="rounded-xl border border-border bg-background/40 p-3 sm:p-4"
                  data-testid={`followup-item-${c.id}`}
                >
                  <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
                    <div className="min-w-0 flex-1">
                      <p className="font-semibold truncate">{c.name}</p>
                      <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-muted-foreground">
                        {c.phone && (
                          <span className="inline-flex items-center gap-1">
                            <Phone className="w-3 h-3" strokeWidth={1.75} />
                            {c.phone}
                          </span>
                        )}
                        {c.email && (
                          <span className="inline-flex items-center gap-1 truncate max-w-[200px]">
                            <Mail className="w-3 h-3" strokeWidth={1.75} />
                            {c.email}
                          </span>
                        )}
                        <span className="inline-flex items-center gap-1">
                          <Calendar className="w-3 h-3" strokeWidth={1.75} />
                          Sist: {fmtDate(c.last_visit)}
                          {c.days_since_last_visit != null && (
                            <span className="text-muted-foreground/70">
                              {" "}({fmtDaysAgo(c.days_since_last_visit)})
                            </span>
                          )}
                        </span>
                        {c.last_service_name && (
                          <span className="inline-flex items-center gap-1">
                            <Scissors className="w-3 h-3" strokeWidth={1.75} />
                            {c.last_service_name}
                          </span>
                        )}
                      </div>
                      <p className="mt-1 text-[11px] text-muted-foreground italic">
                        Ingen kommende booking
                      </p>
                      {suggested && (
                        <blockquote className="mt-2 text-sm border-l-2 border-primary/40 pl-3 text-foreground/85 italic">
                          {suggested}
                        </blockquote>
                      )}
                    </div>
                    <div className="flex flex-wrap gap-2 sm:flex-col sm:items-stretch sm:w-44 shrink-0">
                      <Button
                        size="sm"
                        variant="outline"
                        className="rounded-lg"
                        onClick={() => copyMessage(c)}
                        data-testid={`followup-copy-msg-${c.id}`}
                      >
                        {justCopied === `${c.id}-msg` ? (
                          <><Check className="w-3.5 h-3.5 mr-1" /> Kopiert</>
                        ) : (
                          <><Copy className="w-3.5 h-3.5 mr-1" /> Kopier melding</>
                        )}
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        className="rounded-lg"
                        onClick={() => copyContact(c)}
                        data-testid={`followup-copy-contact-${c.id}`}
                      >
                        {justCopied === `${c.id}-contact` ? (
                          <><Check className="w-3.5 h-3.5 mr-1" /> Kopiert</>
                        ) : (
                          <><Copy className="w-3.5 h-3.5 mr-1" /> Kopier kontaktinfo</>
                        )}
                      </Button>
                      <Button
                        size="sm"
                        className="rounded-lg"
                        disabled={busy === c.id}
                        onClick={() => snooze(c)}
                        data-testid={`followup-snooze-${c.id}`}
                      >
                        <Check className="w-3.5 h-3.5 mr-1" />
                        Marker som fulgt opp
                      </Button>
                    </div>
                  </div>
                </li>
              );
            })}
          </ul>
        )}
        {data?.items?.length > 8 && (
          <p className="mt-3 text-xs text-muted-foreground text-center">
            Viser de 8 eldste · totalt {data.items.length} kunder
          </p>
        )}
      </CardContent>
    </Card>
  );
}
